import { useLocation, useNavigate } from '@remix-run/react';
import { useEffect, useState } from 'react';

import useGlobalModals from '~/components/GlobalModals/hooks/useGlobalModals';

import { LocalStorageKeys } from '~/interfaces/local_storage';
import type { SignupFlow, SignupFlowStep } from '~/interfaces/signup_flow';

import { is_iOS } from '~/utils/deviceTest';
import { substitutePostSlugInUrl } from '~/utils/substitutePostSlugInUrl';

const SIGNUP_FLOW_KEY = 'signup_flow';

type Redirects = {
  id: string;
  startedAt: string;
  steps: SignupFlowStep[];
  currentIndex: number;
  nextIndex: number;
  openExternalLinkInSameTab: boolean;
};

interface ReturnProps {
  currentStep: any;
  nextStep: any;
  isInProgress: boolean;
  steps: any[];
  progress: number;
  startRedirects: (signupFlow: SignupFlow) => void;
  nextPage: () => void;
}

const useSignupRedirects = (): ReturnProps => {
  const navigate = useNavigate();
  const location = useLocation();
  const { handleOpenModal } = useGlobalModals();
  const [redirects, setRedirects] = useState<Redirects>();
  const redirectLength = redirects?.steps?.length ?? 0;
  const stepCount = redirects ? redirects?.currentIndex + 1 : 0;
  const progress = (stepCount / redirectLength) * 100;
  const nextStep = redirects?.steps?.[redirects?.nextIndex];
  const isLastStep = redirects?.nextIndex === (redirects?.steps?.length ?? 0);

  const clearRedirects = () => {
    localStorage.removeItem(SIGNUP_FLOW_KEY);
  };

  const addCurrentSearchParamsToUrl = (
    url: URL,
    { overwriteExisting = false }: { overwriteExisting?: boolean } = {},
  ): URL => {
    if (typeof window === 'undefined') return url;

    const currentParams = new URLSearchParams(window.location.search);
    currentParams.forEach((value, key) => {
      if (key === 'modal') return;

      if (overwriteExisting || !url.searchParams.has(key)) {
        url.searchParams.set(key, value);
      }
    });

    return url;
  };

  const handleExternalRedirect = (url: string, openExternalLinkInSameTab?: boolean) => {
    setTimeout(() => {
      // ios devices don't like window.open, so we redirect them directly
      if (is_iOS(navigator.userAgent)) {
        try {
          window.location.href = url;
        } catch {
          window.location.href = url;
        }
      } else {
        if (openExternalLinkInSameTab ?? redirects?.openExternalLinkInSameTab) {
          window.location.href = url;
        } else {
          window.open(url, '_blank');
          navigate('/');
        }
      }
    }, 1000);
  };

  const handleStartRedirects = (signupFlow: SignupFlow) => {
    // Substitute URL variables in all steps before storing, filtering out invalid steps
    const processedSteps = signupFlow.steps
      .map((step) => {
        const processedUrl = substitutePostSlugInUrl(step.url, location.pathname);
        return processedUrl ? { ...step, url: processedUrl } : null;
      })
      .filter((step): step is SignupFlowStep => step !== null);

    // If no valid steps remain after processing, just redirect to homepage
    if (processedSteps.length === 0) {
      navigate('/');
      return;
    }

    localStorage.setItem(
      SIGNUP_FLOW_KEY,
      JSON.stringify({
        id: signupFlow.id,
        startedAt: new Date().toISOString(),
        steps: processedSteps,
        currentIndex: 0,
        nextIndex: 1,
        openExternalLinkInSameTab: signupFlow.open_external_link_in_same_tab,
      }),
    );

    try {
      const stringifiedEmail = localStorage.getItem(LocalStorageKeys.EMAIL);
      const email = stringifiedEmail && JSON.parse(stringifiedEmail);
      // Use the processed first step URL
      const processedFirstStep = processedSteps[0];
      const url = new URL(processedFirstStep.url);
      url.searchParams.set('email', email);
      addCurrentSearchParamsToUrl(url);

      if (processedFirstStep.external) {
        handleExternalRedirect(url.href, signupFlow.open_external_link_in_same_tab);
      } else {
        const pathname = url.pathname + url.search;
        navigate(pathname);
      }
    } catch {
      navigate('/');
    }
  };

  useEffect(() => {
    const redirectsInProcess = localStorage.getItem(SIGNUP_FLOW_KEY);

    if (redirectsInProcess) {
      const parsedRedirects = JSON.parse(redirectsInProcess);
      const startedAt = parsedRedirects.startedAt;
      const startedAtTime = new Date(startedAt).getTime();
      const now = new Date().getTime();
      const timeSinceStarted = now - startedAtTime;
      const timeLimit = 1000 * 60 * 3; // 3 minutes

      // If the user has been in the middle of the signup flow for more than 3 minutes, remove the redirects from local storage
      // This might happen if the user leaves the page open or comes back
      if (timeSinceStarted > timeLimit) {
        clearRedirects();
      } else {
        setRedirects(JSON.parse(redirectsInProcess));
      }
    } else {
      redirects && setRedirects(undefined);
    }
  }, [location]);

  // When a signup flow starts, we append a banner to the top of the page.
  // Occasionally, users exit the flow by switching the url manually and the banner persists
  // This checks if the url is different than the current step and if so, removes the redirects from local storage
  useEffect(() => {
    if (redirects) {
      const currentStep = redirects.steps[redirects.currentIndex];
      const currentStepPathname = new URL(currentStep.url).pathname;
      const locationSearchParams = new URLSearchParams(location.search);
      const isOnWrongPage = currentStep.modal_param
        ? currentStep.modal_param !== locationSearchParams.get('modal')
        : !currentStepPathname.includes(location.pathname) ||
          (location.pathname === '/' && currentStepPathname !== location.pathname);

      if (isOnWrongPage) {
        clearRedirects();
        setRedirects(undefined);
      }
    }
  }, [redirects]);

  // If already on the last step, clear the redirects
  useEffect(() => {
    if (redirects?.currentIndex === undefined || redirects?.steps?.length === undefined) return;

    if ((redirects?.currentIndex || 0) >= (redirects?.steps?.length || 0) - 1) {
      clearRedirects();
    }
  }, [redirects?.currentIndex, redirects?.steps?.length]);

  return {
    currentStep: redirects?.steps?.[redirects?.currentIndex],
    nextStep,
    isInProgress: Boolean(redirects) && !isLastStep,
    steps: redirects?.steps || [],
    progress: Number(progress.toFixed(1)),
    startRedirects: (signupFlow: SignupFlow) => handleStartRedirects(signupFlow),
    nextPage: () => {
      if (!redirects) return;

      const lastIndex = redirects?.steps?.length - 1;

      if (redirects?.nextIndex > lastIndex) {
        clearRedirects();
        navigate('/');
      } else {
        const stringifiedEmail = localStorage.getItem(LocalStorageKeys.EMAIL);
        const email = stringifiedEmail && JSON.parse(stringifiedEmail);
        const nextRedirect = redirects?.steps?.[redirects?.nextIndex];
        const isNextRedirectExternal = false || nextRedirect?.external;

        if (isNextRedirectExternal) {
          try {
            // Apply slug substitution to external redirect URL as well
            const processedUrl = substitutePostSlugInUrl(nextRedirect.url, location.pathname) || nextRedirect.url;
            const url = new URL(processedUrl);
            url.searchParams.set('email', email);
            addCurrentSearchParamsToUrl(url);
            handleExternalRedirect(url.href);
            clearRedirects();

            if (!redirects?.openExternalLinkInSameTab) {
              navigate('/');
            }
          } catch {
            clearRedirects();
            navigate('/');
          }
        } else {
          try {
            // Apply slug substitution to internal redirect URL
            const processedUrl = substitutePostSlugInUrl(nextRedirect.url, location.pathname) || nextRedirect.url;
            const nextUrl = new URL(processedUrl);
            nextUrl.searchParams.set('email', email);
            addCurrentSearchParamsToUrl(nextUrl);
            if (isLastStep) {
              clearRedirects();
            } else {
              const redirectState = {
                currentIndex: redirects?.currentIndex + 1,
                nextIndex: redirects?.nextIndex + 1,
                steps: redirects?.steps,
                openExternalLinkInSameTab: redirects?.openExternalLinkInSameTab,
              };

              localStorage.setItem(SIGNUP_FLOW_KEY, JSON.stringify(redirectState));
            }

            if (nextRedirect?.modal_param) {
              handleOpenModal(nextRedirect.modal_param);
            } else {
              navigate(nextUrl.pathname + nextUrl.search);
            }
          } catch {
            clearRedirects();
            navigate('/');
          }
        }
      }
    },
  };
};

export default useSignupRedirects;
